import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  ConfigProvider,
  Form,
  FormInstance,
  Input,
} from "antd";
import { BaseViews, IBaseViews } from "app/based/baseViews/BaseViews";
import { AuthenticationRequestEntity } from "features/login/domain/entities/AuthenticationRequestEntity";
import { AuthenticationResponseEntity } from "features/login/domain/entities/AuthenticationResponseEntity";
import { isEmpty } from "lodash";
import { createRef, ReactNode } from "react";
import { FaRegUser } from "react-icons/fa";
import { GrSecure } from "react-icons/gr";
import { IoMailOutline, IoPhonePortraitOutline } from "react-icons/io5";
import { MdOutlineLockClock } from "react-icons/md";
import { ClassFormat } from "utils/format/ClassFormat";
import { colorFormat } from "utils/format/ColorFormat";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { ILoginFeature, ILoginFeatureState } from "../interface/ILoginFeature";
import { LoginViewModel } from "../viewModel/LoginViewModel";

export default class LoginFeature extends BaseViews<
  LoginViewModel,
  ILoginFeature,
  ILoginFeatureState
> {
  constructor(props: IBaseViews & ILoginFeature) {
    super(props);
    strings.setLanguage("en");
    this.state = {
      loading: false,
      error: {},
      savedForNextLogin: true,
      signUpMode: false,
    };
    this.onSumbit = this.onSumbit.bind(this);
    this.onNavigate = this.onNavigate.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSignUpModeChange = this.onSignUpModeChange.bind(this);
  }

  private form = createRef<FormInstance>();

  protected override bindViewModel(): void {
    super.bindViewModel();
    this.subscribeToVM(this.viewModel.input.loading, (res) =>
      this.setState({ loading: res }),
    );

    this.subscribeToVM(this.viewModel.output.onLoginSuccess, async (res) => {
      if (!isEmpty(res)) {
        await this.onNavigate(res);
      }
    });

    this.subscribeToVM(this.viewModel.output.onSignUpSuccess, async (res) => {
      if (!isEmpty(res)) {
        getMessage(res, 3, "success");
        this.setState({
          signUpMode: false,
        });
      }
    });

    this.subscribeToVM(this.viewModel.output.onError, (res) => {
      if (!isEmpty(res)) {
        getMessage(res?.message, 3, "error");
      }
    });
  }

  private onChange() {
    this.setState({
      error: {},
    });
  }

  private async onNavigate(user: AuthenticationResponseEntity) {
    const { navigate } = this.props;
    const { getRoles } = this.context;
    await getRoles?.().then(() => navigate("/"));
  }

  private onSumbit = (form: FormInstance<any>) => {
    const { signUpMode, savedForNextLogin } = this.state;
    const formVal = form?.getFieldsValue(true);
    const isSignUp = signUpMode === true;
    if (isSignUp) {
      this.connectToVM(this.viewModel.input.signUp, formVal);
    } else {
      this.connectToVM(this.viewModel.input.login, {
        ...formVal,
        isSavedForNextLogin: savedForNextLogin,
      } as AuthenticationRequestEntity);
    }
  };

  private onSignUpModeChange = () => {
    this.form.current?.resetFields();
    this.setState({
      signUpMode: !this.state.signUpMode,
    });
  };

  private appVersionSection = () => {
    const { versionNumber } = this.context;
    return (
      <div className="flex justify-center items-center mt-5">
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-[#f8fafc] to-[#eef2ff] border border-[#e2e8f0] shadow-sm">
          <div className="w-[9px] h-[9px] rounded-full bg-green-600 animate-pulse" />
          <span className="text-[12px] font-medium text-[#64748b] tracking-wide">
            Version {versionNumber}
          </span>
        </div>
      </div>
    );
  };

  render(): ReactNode {
    const { error, loading, savedForNextLogin } = this.state;
    return (
      <section className="w-full flex flex-col h-screen justify-start items-center">
        <div className="w-full xl:h-[110px] h-[80px] flex justify-start items-center border-b border-[#0000001A] shadow-md">
          {/* <a
            className="flex xl:px-36 px-5 w-full"
            href="https://phuongquan.vn/"
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={companyLogo}
              alt="company-logo"
              className="object-contain xl:w-[80px] w-[60px] xl:h-[80px] h-[60px]"
            />
          </a> */}
        </div>
        <div className="w-full h-full flex flex-col items-center justify-center gap-y-2 xl:px-0 px-2 login-ui">
          <h1 className="font-[600] xl:text-[25px] text-[20px]">
            {this.state.signUpMode
              ? strings.GlobalMessage.SignUp
              : strings.GlobalLabels.Welcome}
          </h1>
          <div className="w-full xl:w-1/3 lg:w-1/2 md:w-2/3 sm:w-2/3 rounded-lg mx-4 border-[1px] border-[#FFFFFF] shadow-lg bg-white">
            <div className="m-auto lg:p-11 p-5">
              <Form
                ref={this.form}
                onFinish={() => this.onSumbit(this.form.current)}
              >
                <div className="flex justify-start items-center w-full gap-x-1">
                  <FaRegUser className={ClassFormat.inputIcon} />
                  <p className={ClassFormat.inputLabel}>
                    {strings.GlobalPlaceholder.UserName}
                  </p>
                </div>
                <div className="w-full">
                  <Form.Item
                    name="username"
                    className="h-18 pb-1 hover:scale-105 transition-all"
                    validateStatus={error.name ? "error" : undefined}
                    style={{ display: "inline" }}
                    rules={[
                      {
                        required: true,
                        type: "string",
                        message: strings.GlobalPlaceholder.UserName,
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      className={ClassFormat.loginInput}
                      placeholder={strings.GlobalPlaceholder.UserName}
                      onChange={this.onChange}
                    />
                  </Form.Item>
                </div>
                <div className="flex justify-start items-center w-full gap-x-1 mt-4">
                  <MdOutlineLockClock className={ClassFormat.inputIcon} />
                  <p className={ClassFormat.inputLabel}>
                    {strings.GlobalPlaceholder.Password}
                  </p>
                </div>
                <div className="w-full">
                  <Form.Item
                    name="password"
                    className="h-18 pb-4 hover:scale-105 transition-all"
                    validateStatus={error.password ? "error" : undefined}
                    style={{ display: "inline" }}
                    rules={[
                      {
                        required: true,
                        type: "string",
                        message: strings.GlobalPlaceholder.Password,
                      },
                    ]}
                  >
                    <>
                      <Input.Password
                        size="large"
                        className={ClassFormat.loginInput}
                        placeholder={strings.GlobalPlaceholder.Password}
                        iconRender={(visible) =>
                          visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                        }
                        onChange={this.onChange}
                      />
                    </>
                  </Form.Item>
                </div>
                {this.state.signUpMode && (
                  <>
                    <div className="flex justify-start items-center w-full gap-x-1 mt-4">
                      <GrSecure className={ClassFormat.inputIcon} />
                      <p className={ClassFormat.inputLabel}>
                        {strings.GlobalLabels.PasswordConfirmed}
                      </p>
                    </div>
                    <div className="w-full">
                      <Form.Item
                        name="passwordConfirmed"
                        className="h-18 pb-1 hover:scale-105 transition-all"
                        validateStatus={error.name ? "error" : undefined}
                        style={{ display: "inline" }}
                        rules={[
                          {
                            required: true,
                            type: "string",
                            message:
                              strings.GlobalPlaceholder.PasswordConfirmed,
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (
                                value &&
                                value !== getFieldValue("password")
                              ) {
                                return Promise.reject(
                                  new Error(
                                    strings.GlobalPlaceholder.PasswordConfirmed,
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Input.Password
                          size="large"
                          className={ClassFormat.loginInput}
                          placeholder={
                            strings.GlobalPlaceholder.PasswordConfirmed
                          }
                          iconRender={(visible) =>
                            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                          }
                          onChange={this.onChange}
                        />
                      </Form.Item>
                    </div>
                    <div className="flex justify-start items-center w-full gap-x-1 mt-4">
                      <FaRegUser className={ClassFormat.inputIcon} />
                      <p className={ClassFormat.inputLabel}>
                        {strings.GlobalLabels.Fullname}
                      </p>
                    </div>
                    <div className="w-full">
                      <Form.Item
                        name="name"
                        className="h-18 pb-1 hover:scale-105 transition-all"
                        validateStatus={error.name ? "error" : undefined}
                        style={{ display: "inline" }}
                        rules={[
                          {
                            required: true,
                            type: "string",
                            message:
                              strings.GlobalLabels.Placeholder +
                              strings.GlobalLabels.Fullname.toLowerCase(),
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          className={ClassFormat.loginInput}
                          placeholder={
                            strings.GlobalLabels.Placeholder +
                            strings.GlobalLabels.Fullname.toLowerCase()
                          }
                          onChange={this.onChange}
                        />
                      </Form.Item>
                    </div>
                    <div className="flex justify-start items-center w-full gap-x-1 mt-4">
                      <IoMailOutline className={ClassFormat.inputIcon} />
                      <p className={ClassFormat.inputLabel}>
                        {strings.GlobalLabels.Email}
                      </p>
                    </div>
                    <div className="w-full">
                      <Form.Item
                        name="email"
                        className="h-18 pb-1 hover:scale-105 transition-all"
                        validateStatus={error.name ? "error" : undefined}
                        style={{ display: "inline" }}
                        rules={[
                          {
                            required: true,
                            type: "string",
                            message: strings.GlobalPlaceholder.Email,
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          className={ClassFormat.loginInput}
                          placeholder={strings.GlobalPlaceholder.Email}
                          onChange={this.onChange}
                        />
                      </Form.Item>
                    </div>
                    <div className="flex justify-start items-center w-full gap-x-1 mt-4">
                      <IoPhonePortraitOutline
                        className={ClassFormat.inputIcon}
                      />
                      <p className={ClassFormat.inputLabel}>
                        {strings.GlobalLabels.Phone}
                      </p>
                    </div>
                    <div className="w-full">
                      <Form.Item
                        name="phone"
                        className="h-18 pb-1 hover:scale-105 transition-all"
                        validateStatus={error.name ? "error" : undefined}
                        style={{ display: "inline" }}
                        rules={[
                          {
                            required: true,
                            type: "string",
                            message: strings.GlobalPlaceholder.Phone,
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          className={ClassFormat.loginInput}
                          placeholder={strings.GlobalPlaceholder.Phone}
                          onChange={this.onChange}
                        />
                      </Form.Item>
                    </div>
                  </>
                )}
                <div className="w-full flex justify-between items-center mt-5 mb-2">
                  {/* {this.state.signUpMode ? (
                    <div className="w-4" />
                  ) : (
                    <div className="flex gap-2">
                      <Checkbox
                        checked={savedForNextLogin}
                        onChange={(e) => {
                          this.setState({
                            savedForNextLogin: e.target.checked,
                          });
                        }}
                        defaultChecked={savedForNextLogin}
                      />
                      <p>{strings.GlobalMessage.SavingForLogin}</p>
                    </div>
                  )}
                  <div
                    className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                    onClick={() => this.onSignUpModeChange()}
                  >
                    {this.state.signUpMode
                      ? strings.GlobalLabels.Login
                      : strings.GlobalMessage.SignUp}
                  </div> */}
                  <div className="flex gap-2">
                    <Checkbox
                      checked={savedForNextLogin}
                      onChange={(e) => {
                        this.setState({
                          savedForNextLogin: e.target.checked,
                        });
                      }}
                      defaultChecked={savedForNextLogin}
                    />
                    <p>{strings.GlobalMessage.SavingForLogin}</p>
                  </div>
                </div>
                <Form.Item style={{ display: "inline" }}>
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: colorFormat?.Blue,
                      },
                    }}
                  >
                    <Button
                      type="primary"
                      size="middle"
                      className="font-semibold hover:scale-105 mt-3 transition-all w-full h-[50px]"
                      loading={loading}
                      htmlType="submit"
                    >
                      {this.state.signUpMode
                        ? strings.GlobalLabels.Confirmed
                        : strings.GlobalLabels.Login}
                    </Button>
                  </ConfigProvider>
                </Form.Item>
              </Form>
              {this.appVersionSection()}
            </div>
          </div>
        </div>
      </section>
    );
  }
}
